import * as React from "react";
import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "ui/sheet";
import { EmptyState } from "../shared/empty-state";
import AppConstant from "../Globals/AppConstants";
import { Button } from "ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "ui/select";
import { LoadingOverlay } from "../shared/loading-overlay";
import { DataTable } from "../shared/data-table";
import { ColumnDef } from "@tanstack/react-table";
import moment from "moment";

export interface EmployeeSalesHistorySheetProps {
  open: boolean;
  onClose: () => void;
  employee: any;
  salesData: any;
  loading: boolean;
  onMonthChange: (month: string, year: number) => void;
  onPageChange: (month: string, year: number, page: number) => void;
  pageSize?: number;
}

const FINANCIAL_YEAR_MONTHS = [
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
  "January",
  "February",
  "March",
];

export const getCurrentMonth = () => {
  const now = new Date();
  const month = now.getMonth(); // 0-based (0 = Jan, 11 = Dec)

  // Financial year starts from April
  // Jan-Mar: Current Calendar Year, Prev Financial Year
  // Apr-Dec: Current Calendar Year, Current Financial Year
  if (month >= 3) {
    // Apr-Dec
    return FINANCIAL_YEAR_MONTHS[month - 3]; // 0-8 for Apr-Dec
  } else {
    // Jan-Mar
    return FINANCIAL_YEAR_MONTHS[month + 9]; // 9-11 for Jan-Mar
  }
};

export const getCurrentFinancialYear = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  if (month >= 3) {
    // Apr-Dec: Current calendar year is the FY start year
    return year;
  } else {
    // Jan-Mar: Previous calendar year is the FY start year
    return year - 1;
  }
};

export function EmployeeSalesHistorySheet({
  open,
  onClose,
  employee,
  salesData,
  loading,
  onMonthChange,
  onPageChange,
  pageSize = 10,
}: EmployeeSalesHistorySheetProps) {
  const [selectedMonth, setSelectedMonth] = useState<string>(getCurrentMonth());
  const [financialYear] = useState<number>(getCurrentFinancialYear());
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    if (selectedMonth) {
      setPage(1);
      onMonthChange(selectedMonth, financialYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  // Reset paging state whenever the sheet is opened for a (possibly different) employee.
  useEffect(() => {
    if (open) {
      setPage(1);
    }
  }, [open, employee?.employeeId]);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    onPageChange(selectedMonth, financialYear, nextPage);
  };

  const bills = salesData?.bills || [];
  const summary = salesData?.summary || { totalBills: 0, totalSalesValue: 0 };
  const hasBills = (bills || []).length > 0;

  const columns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      {
        id: "invoiceId",
        header: "Bill Number",
        cell: ({ row }) => row.original?.invoiceId || "N/A",
        meta: { width: "120px" },
      },
      {
        id: "billDate",
        header: "Bill Date",
        cell: ({ row }) =>
          row.original?.billDate
            ? moment(row.original.billDate).format("DD-MMM-YYYY")
            : "N/A",
        meta: { width: "120px" },
      },
      {
        id: "customerName",
        header: "Customer Name",
        cell: ({ row }) => row.original?.customerName || "N/A",
        meta: { width: "200px" },
      },
      {
        id: "totalAmount",
        header: "Bill Amount",
        cell: ({ row }) => (
          <div className="text-right tabular-nums">
            ₹{Number(row.original?.totalAmount || 0).toFixed(2)}
          </div>
        ),
        meta: { width: "120px" },
      },
      {
        id: "paymentMode",
        header: "Payment Mode",
        cell: ({ row }) => row.original?.paymentMode || "N/A",
        meta: { width: "120px" },
      },
    ],
    []
  );

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="flex h-full w-full flex-col gap-0 p-0 sm:max-w-xl">
        <LoadingOverlay loading={loading} />
        <SheetHeader className="shrink-0 border-b px-4 py-3">
          <SheetTitle className="text-base">Sales History</SheetTitle>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-3 overflow-hidden p-4 min-h-0">
          {/* Employee Info + Summary in one compact row */}
          <div className="shrink-0 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3">
            <div className="space-y-0.5">
              <div className="text-sm font-medium text-foreground">{employee?.name || "N/A"}</div>
              <div className="text-xs text-muted-foreground">
                {employee?.mobileNumber || "N/A"}
              </div>
            </div>
            <div className="flex gap-4 text-right">
              <div>
                <div className="text-[11px] text-muted-foreground">Total Bills</div>
                <div className="text-base font-semibold text-foreground">
                  {summary?.totalBills || 0}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground">Total Sales Value</div>
                <div className="text-base font-semibold text-foreground">
                  ₹{Number(summary?.totalSalesValue || 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* Month Filter */}
          <div className="shrink-0 flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground shrink-0">Month</label>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="h-8 w-full text-sm">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent>
                {FINANCIAL_YEAR_MONTHS.map((month) => (
                  <SelectItem key={month} value={month}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Bills Table */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-sm text-muted-foreground">Loading bills...</div>
            </div>
          ) : hasBills ? (
            <div className="flex flex-1 flex-col gap-2 min-h-0">
              <div className="shrink-0 text-xs font-medium text-muted-foreground">Bills in {selectedMonth}</div>
              <DataTable
                columns={columns}
                data={bills}
                loading={loading}
                className="flex-1 min-h-0 text-sm"
                fillHeight
                pagination={{
                  page,
                  total: salesData?.pagination?.totalRecords ?? summary?.totalBills ?? 0,
                  pageSize,
                  onChange: handlePageChange,
                }}
              />
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="text-sm text-muted-foreground">
                  No sales found for {selectedMonth}
                </div>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default EmployeeSalesHistorySheet;
